import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { ArrowUpRight, Award, BarChart3, Bell, BriefcaseBusiness, ChevronRight, Download, Gauge, KeyRound, LayoutDashboard, LogOut, Menu, Plus, Search, ShieldCheck, Sparkles, Trash2, TrendingDown, TrendingUp, Users, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (value = 0) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(Number(value) || 0);
const signedMoney = (value = 0) => `${Number(value) >= 0 ? "+" : "-"}${money(Math.abs(Number(value) || 0))}`;
const pct = (value = 0) => `${Number(value) >= 0 ? "+" : ""}${Number(value || 0).toFixed(2)}%`;

async function request(path, options = {}) {
  const isForm = options.body instanceof FormData;
  const response = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: isForm ? { ...(options.headers || {}) } : { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(typeof data.detail === "string" ? data.detail : "Request failed");
  }
  if (response.status === 204) return null;
  return response.json();
}

function Candles() {
  return (
    <div className="candle-field" aria-hidden="true">
      {Array.from({ length: 22 }).map((_, i) => (
        <span
          className={`candle ${i % 4 === 0 ? "down" : "up"}`}
          style={{ left: `${i * 5 - 2}%`, height: `${38 + (i % 5) * 10}px`, animationDelay: `${i * -0.35}s` }}
          key={`candle-${i}`}
        >
          <i />
        </span>
      ))}
    </div>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await request("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
      onLogin(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <main className="login-screen">
      <Candles />
      <div className="login-brand"><div className="brand-mark">V</div><span>VENTREX</span></div>
      <section className="login-panel">
        <div className="eyebrow"><ShieldCheck size={14} /> PRIVATE CLIENT PORTAL</div>
        <h1>Your Accounts.<br /><em>Your Progress.</em><br />Your Results.</h1>
        <p className="login-copy">Private performance, professional results. Access your curated account overview securely.</p>
        <form onSubmit={submit} data-testid="login-form">
          <label>Email address
            <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required />
          </label>
          <label>Password
            <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required />
          </label>
          {error && <div className="form-error" data-testid="login-error">{error}</div>}
          <button className="gold-button full" data-testid="login-submit-button" disabled={loading}>
            {loading ? "Authenticating…" : "Enter VENTREX"}<ArrowUpRight size={17} />
          </button>
        </form>
        <div className="login-foot"><KeyRound size={13} /> Invitation-only access · USD reporting</div>
      </section>
      <div className="login-note">{new Date().getFullYear()} VENTREX <span>•</span> Private Performance. Professional Results.</div>
    </main>
  );
}

const clientNav = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "accounts", label: "Accounts", icon: BriefcaseBusiness },
  { id: "performance", label: "Performance", icon: BarChart3 },
  { id: "achievements", label: "Achievements", icon: Award },
];
const adminNav = [
  { id: "admin", label: "Command center", icon: LayoutDashboard },
  { id: "clients", label: "Clients", icon: Users },
  { id: "accounts", label: "Accounts", icon: BriefcaseBusiness },
  { id: "audit", label: "Audit trail", icon: ShieldCheck },
];

function Shell({ user, active, setActive, onLogout, children }) {
  const [mobileNav, setMobileNav] = useState(false);
  const items = user.role === "admin" ? adminNav : clientNav;
  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNav ? "open" : ""}`}>
        <div className="side-brand">
          <div className="brand-mark small">V</div>
          <div><b>VENTREX</b><span>PRIVATE PORTAL</span></div>
          <button className="icon-button mobile-close" onClick={() => setMobileNav(false)} data-testid="close-mobile-nav"><X size={18} /></button>
        </div>
        <div className="side-rule" />
        <div className="side-label">{user.role === "admin" ? "Administration" : "Your workspace"}</div>
        <nav>
          {items.map(({ id, label, icon: Icon }) => (
            <button key={id} className={active === id ? "nav-active" : ""} onClick={() => { setActive(id); setMobileNav(false); }} data-testid={`nav-${id}`}>
              <Icon size={17} />{label}
              {active === id && <ChevronRight size={14} className="nav-arrow" />}
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <div className="security-chip"><ShieldCheck size={16} /><span><b>Secure workspace</b><small>Private & encrypted</small></span></div>
          <button className="logout-button" onClick={onLogout} data-testid="logout-button"><LogOut size={16} /> Sign out</button>
        </div>
      </aside>
      {mobileNav && <div className="nav-overlay" onClick={() => setMobileNav(false)} />}
      <div className="content-shell">
        <header className="topbar">
          <button className="icon-button menu-toggle" onClick={() => setMobileNav(true)} data-testid="open-mobile-nav"><Menu size={20} /></button>
          <div className="breadcrumbs"><span>VENTREX</span><ChevronRight size={14} /><b>{items.find((item) => item.id === active)?.label || "Account"}</b></div>
          <div className="top-actions">
            <div className="user-pill">
              <div className="avatar">{(user.name || user.email || "V")[0].toUpperCase()}</div>
              <span><b>{user.name || "Administrator"}</b><small>{user.role === "admin" ? "Portfolio manager" : "VIP client"}</small></span>
            </div>
            <button className="icon-button" data-testid="notifications-button"><Bell size={17} /></button>
          </div>
        </header>
        <main className="page-content">{children}</main>
      </div>
    </div>
  );
}

function Metric({ label, value, detail, trend, tone = "gold", icon: Icon }) {
  return (
    <article className="metric-card" data-testid={`metric-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <div className="metric-head"><span>{label}</span>{Icon && <Icon size={16} className={`metric-icon ${tone}`} />}</div>
      <strong className={tone}>{value}</strong>
      {detail && (
        <div className="metric-detail">
          {trend !== undefined && (Number(trend) >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />)}
          <span>{detail}</span>
        </div>
      )}
    </article>
  );
}

function StatusBadge({ children }) {
  const key = String(children || "").toLowerCase().replaceAll(" ", "-");
  return <span className={`status status-${key}`} data-testid={`status-${key}`}>{children}</span>;
}

function Journey({ stage }) {
  const stages = ["PHASE 1", "PHASE 2", "FUNDED"];
  const current = stages.indexOf(stage);
  return (
    <div className="journey-steps">
      {stages.map((item, index) => (
        <div className={`journey-step ${index <= current ? "complete" : ""}`} key={item}>
          <span>{index < current ? "✓" : index + 1}</span><small>{item}</small>
        </div>
      ))}
    </div>
  );
}

function Empty({ text }) {
  return <div className="empty-state" data-testid="empty-state"><Sparkles size={18} /><span>{text}</span></div>;
}

function AccountCard({ account, onOpen }) {
  return (
    <article className="account-card" data-testid={`account-card-${account.id}`}>
      <div className="account-top">
        <div><div className="firm-name">{account.prop_firm}</div><div className="account-name">{account.account_name}</div></div>
        <StatusBadge>{account.status}</StatusBadge>
      </div>
      <div className="account-balance"><span>Current balance</span><strong>{money(account.current_balance)}</strong></div>
      <div className="account-grid">
        <div><span>Account size</span><b>{money(account.account_size)}</b></div>
        <div><span>P&L</span><b className={account.pnl >= 0 ? "positive" : "negative"}>{signedMoney(account.pnl)}</b></div>
        <div><span>Return</span><b className={account.pnl_pct >= 0 ? "positive" : "negative"}>{pct(account.pnl_pct)}</b></div>
      </div>
      <div className="account-footer">
        <span className="stage-label"><i className="stage-dot" />{account.stage}</span>
        <button className="text-button" onClick={() => onOpen(account)} data-testid={`view-account-${account.id}`}>View account <ArrowUpRight size={14} /></button>
      </div>
    </article>
  );
}

function ClientOverview({ user, accounts, notes, setActive, onOpen }) {
  const total = (key) => accounts.reduce((sum, account) => sum + Number(account[key] || 0), 0);
  const funded = accounts.filter((a) => a.status === "FUNDED").length;
  const overall = total("account_size") ? (total("pnl") / total("account_size")) * 100 : 0;
  return (
    <>
      <div className="page-intro reveal">
        <div>
          <div className="eyebrow">{new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} <span>•</span> Portfolio overview</div>
          <h1>Good evening, <em>{user.name?.split(" ")[0] || "there"}.</em></h1>
          <p>Your Accounts. Your Progress. Your Results.</p>
        </div>
        <div className="intro-actions">
          <span className="live-dot"><i /> Private workspace</span>
          <button className="outline-button" onClick={() => setActive("accounts")} data-testid="overview-accounts-button">View all accounts <ArrowUpRight size={15} /></button>
        </div>
      </div>
      <div className="metrics-grid reveal delay-1">
        <Metric label="Total account size" value={money(total("account_size"))} detail={`${accounts.length} managed accounts`} icon={BriefcaseBusiness} />
        <Metric label="Current balance" value={money(total("current_balance"))} detail={`${pct(overall)} overall return`} trend={overall} icon={Gauge} tone="green" />
        <Metric label="Total P&L" value={signedMoney(total("pnl"))} detail="Since inception" trend={total("pnl")} icon={TrendingUp} tone={total("pnl") >= 0 ? "green" : "red"} />
        <Metric label="Funded accounts" value={funded.toString().padStart(2, "0")} detail={`${accounts.filter((a) => a.stage === "PHASE 2").length} in Phase 2`} icon={Sparkles} />
      </div>
      <div className="section-heading">
        <div><span className="eyebrow">Portfolio snapshot</span><h2>Your accounts</h2></div>
        <button className="text-button" onClick={() => setActive("accounts")} data-testid="overview-see-all">See all accounts <ArrowUpRight size={15} /></button>
      </div>
      <div className="account-cards reveal delay-2">
        {accounts.slice(0, 3).map((account) => <AccountCard account={account} onOpen={onOpen} key={account.id} />)}
      </div>
      <div className="lower-grid reveal delay-3">
        <section className="panel journey-panel">
          <div className="panel-heading"><div><span className="eyebrow">Progression</span><h3>Account journey</h3></div><Award size={19} className="gold-icon" /></div>
          {accounts.length ? (
            <div className="journey-list">
              {accounts.slice(0, 2).map((account) => (
                <div className="journey-row" key={account.id}>
                  <div className="journey-name"><b>{account.prop_firm}</b><small>{account.account_name}</small></div>
                  <Journey stage={account.stage} />
                </div>
              ))}
            </div>
          ) : <Empty text="Your account journey will appear here." />}
        </section>
        <section className="panel updates-panel">
          <div className="panel-heading"><div><span className="eyebrow">From your manager</span><h3>Latest updates</h3></div><Bell size={18} className="gold-icon" /></div>
          {notes.length ? notes.slice(0, 3).map((note) => (
            <div className="update-row" key={note.id}>
              <div className="update-icon"><Sparkles size={14} /></div>
              <div><p>{note.message}</p><small>{new Date(note.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</small></div>
            </div>
          )) : <Empty text="No new updates from your manager." />}
        </section>
      </div>
    </>
  );
}

function ClientAccounts({ accounts, onOpen }) {
  const [query, setQuery] = useState("");
  const filtered = accounts.filter((a) => `${a.prop_firm} ${a.account_name}`.toLowerCase().includes(query.toLowerCase()));
  return (
    <>
      <div className="page-intro">
        <div>
          <div className="eyebrow">Portfolio</div>
          <h1>Account <em>directory.</em></h1>
          <p>Every account is tracked independently and updated by your manager.</p>
        </div>
      </div>
      <div className="filter-bar">
        <div className="search-box"><Search size={16} /><input data-testid="account-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search prop firm or account" /></div>
        <span className="result-count">{filtered.length} accounts</span>
      </div>
      <div className="account-cards">
        {filtered.map((account) => <AccountCard account={account} onOpen={onOpen} key={account.id} />)}
      </div>
    </>
  );
}

function AccountDetail({ account, detail, onBack }) {
  const viewFile = (cert) => {
    if (!cert.file_id) { toast.info("No file attached to this certificate."); return; }
    window.open(`${API}/files/${cert.file_id}`, "_blank");
  };
  const certs = detail?.achievements || [];
  const certsByStage = CERT_CATEGORIES.map((cat) => ({ ...cat, items: certs.filter((c) => (c.stage || "").toUpperCase() === cat.id) }));
  return (
    <>
      <button className="back-link" onClick={onBack} data-testid="back-to-accounts"><ChevronRight size={15} className="back-chevron" /> Back to accounts</button>
      <div className="detail-hero">
        <div>
          <div className="eyebrow">{account.prop_firm} <span>•</span> {account.reference || "Managed account"}</div>
          <h1>{account.account_name}</h1>
          <p>Independent account record · Last updated {new Date(account.updated_at).toLocaleDateString()}</p>
        </div>
        <StatusBadge>{account.status}</StatusBadge>
      </div>
      <div className="detail-metrics">
        <Metric label="Account size" value={money(account.account_size)} />
        <Metric label="Current balance" value={money(account.current_balance)} tone="green" />
        <Metric label="Starting balance" value={money(account.starting_balance)} />
        <Metric label="Current P&L" value={signedMoney(account.pnl)} tone={account.pnl >= 0 ? "green" : "red"} detail={pct(account.pnl_pct)} />
      </div>
      <div className="detail-columns">
        <div>
          <section className="panel">
            <div className="panel-heading"><div><span className="eyebrow">Recognition</span><h3>Certificates & documents</h3></div><Award size={18} className="gold-icon" /></div>
            <div className="client-cert-grid">
              {certsByStage.map((cat) => (
                <div className={`client-cert-card ${cat.items.length ? "has" : "empty"}`} key={cat.id} data-testid={`client-cert-cat-${cat.id.toLowerCase().replaceAll(" ", "-")}`}>
                  <div className="cert-card-head">
                    <span className={`cert-tag cert-tag-${cat.id.toLowerCase().replaceAll(" ", "-")}`}>{cat.id}</span>
                    <b>{cat.label}</b>
                  </div>
                  {cat.items.length ? cat.items.map((item) => (
                    <div className="cert-item" key={item.id} data-testid={`client-cert-${item.id}`}>
                      <div>
                        <b>{item.title}</b>
                        <small>{item.description || item.file_name || "Certificate on file"}</small>
                        <small className="cert-date">{new Date(item.created_at).toLocaleDateString()}</small>
                      </div>
                      {item.file_id && <button className="mini-button gold" onClick={() => viewFile(item)} data-testid={`client-cert-view-${item.id}`}>View</button>}
                    </div>
                  )) : <small className="cert-empty">Not issued yet</small>}
                </div>
              ))}
            </div>
          </section>
        </div>
        <aside className="detail-side">
          <section className="panel journey-detail">
            <div className="panel-heading"><div><span className="eyebrow">Account journey</span><h3>Progress</h3></div></div>
            <Journey stage={account.stage} />
            <div className="data-list">
              <div><span>Stage</span><b>{account.stage}</b></div>
              <div><span>Profit target</span><b>{money(account.profit_target)}</b></div>
              <div><span>Current drawdown</span><b className="negative">{money(account.current_drawdown)}</b></div>
              <div><span>Remaining drawdown</span><b>{money(account.remaining_drawdown)}</b></div>
              <div><span>Trading days</span><b>{account.trading_days}</b></div>
            </div>
          </section>
        </aside>
      </div>
    </>
  );
}

function ClientPerformance({ accounts, onOpen }) {
  return (
    <>
      <div className="page-intro">
        <div>
          <div className="eyebrow">Analytics</div>
          <h1>Performance <em>review.</em></h1>
          <p>Read your progress account by account, without the noise.</p>
        </div>
      </div>
      <div className="performance-grid">
        {accounts.map((account) => (
          <div className="panel performance-row" key={account.id}>
            <div><span className="eyebrow">{account.prop_firm}</span><h3>{account.account_name}</h3></div>
            <div className="performance-value">
              <b className={account.pnl >= 0 ? "positive" : "negative"}>{signedMoney(account.pnl)}</b>
              <small>{pct(account.pnl_pct)} return</small>
            </div>
            <div className="mini-progress"><span style={{ width: `${Math.min(100, Math.max(8, Math.abs(account.pnl_pct) * 20))}%` }} /></div>
            <button className="icon-button" onClick={() => onOpen(account)} data-testid={`performance-account-${account.id}`}><ArrowUpRight size={16} /></button>
          </div>
        ))}
      </div>
    </>
  );
}

function ClientAchievements({ accounts, onOpen }) {
  const [certs, setCerts] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const all = [];
        for (const account of accounts) {
          const list = await request(`/accounts/${account.id}/achievements`);
          list.forEach((c) => all.push({ ...c, account }));
        }
        setCerts(all);
      } catch (err) { toast.error(err.message); }
    })();
  }, [accounts]);
  const viewFile = (cert) => {
    if (!cert.file_id) { toast.info("No file attached to this certificate."); return; }
    window.open(`${API}/files/${cert.file_id}`, "_blank");
  };
  return (
    <>
      <div className="page-intro">
        <div>
          <div className="eyebrow">Recognition</div>
          <h1>Achievements <em>& certificates.</em></h1>
          <p>Every Phase 1, Phase 2, Funded and Payout certificate your manager issues appears here.</p>
        </div>
      </div>
      <div className="achievement-cat-grid">
        {CERT_CATEGORIES.map((cat) => {
          const items = certs.filter((c) => (c.stage || "").toUpperCase() === cat.id);
          return (
            <section className="panel" key={cat.id} data-testid={`achievement-cat-${cat.id.toLowerCase().replaceAll(" ", "-")}`}>
              <div className="panel-heading">
                <div><span className="eyebrow">{cat.id}</span><h3>{cat.label}</h3></div>
                <Award size={18} className="gold-icon" />
              </div>
              {items.length ? items.map((item) => (
                <div className="cert-item" key={item.id} data-testid={`achievement-cert-${item.id}`}>
                  <div>
                    <b>{item.title}</b>
                    <small>{item.account?.prop_firm} · {item.account?.account_name}</small>
                    <small>{item.description || item.file_name || "Certificate on file"}</small>
                    <small className="cert-date">{new Date(item.created_at).toLocaleDateString()}</small>
                  </div>
                  {item.file_id && <button className="mini-button gold" onClick={() => viewFile(item)} data-testid={`achievement-view-${item.id}`}>View</button>}
                </div>
              )) : (
                <div className="achievement-placeholder">
                  <Award size={26} />
                  <b>Not issued yet</b>
                  <span>Your manager will upload {cat.label.toLowerCase()} here when available.</span>
                  {accounts[0] && <button className="text-button" onClick={() => onOpen(accounts[0])} data-testid={`achievement-open-${cat.id.toLowerCase().replaceAll(" ", "-")}`}>Open an account <ArrowUpRight size={14} /></button>}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </>
  );
}

function AdminDashboard({ summary, accounts, setActive }) {
  const overall = summary.account_size ? (summary.pnl / summary.account_size) * 100 : 0;
  return (
    <>
      <div className="page-intro reveal">
        <div>
          <div className="eyebrow">Command center <span>•</span> {new Date().toLocaleDateString()}</div>
          <h1>Good evening, <em>Admin.</em></h1>
          <p>One clear view of the VENTREX client portfolio.</p>
        </div>
        <div className="intro-actions">
          <span className="live-dot"><i /> All systems operational</span>
          <button className="gold-button" onClick={() => setActive("accounts")} data-testid="admin-update-accounts-button"><Plus size={16} /> Update account</button>
        </div>
      </div>
      <div className="metrics-grid reveal delay-1">
        <Metric label="Total clients" value={String(summary.clients).padStart(2, "0")} detail="Active client records" icon={Users} />
        <Metric label="Account size" value={money(summary.account_size)} detail={`${summary.accounts} managed accounts`} icon={BriefcaseBusiness} />
        <Metric label="Combined balance" value={money(summary.balance)} detail={`${pct(overall)} overall`} trend={overall} tone="green" icon={Gauge} />
        <Metric label="Combined P&L" value={signedMoney(summary.pnl)} detail={summary.funded + " funded"} trend={summary.pnl} tone={summary.pnl >= 0 ? "green" : "red"} icon={TrendingUp} />
      </div>
      <div className="admin-grid">
        <section className="panel admin-stat-panel">
          <div className="panel-heading"><div><span className="eyebrow">Portfolio mix</span><h3>Account stages</h3></div><BriefcaseBusiness size={18} className="gold-icon" /></div>
          <div className="stage-bars">
            <div><span>Funded <b>{summary.funded}</b></span><i><em style={{ width: `${summary.accounts ? (summary.funded / summary.accounts) * 100 : 0}%` }} /></i></div>
            <div><span>Phase 2 <b>{summary.phase2}</b></span><i><em style={{ width: `${summary.accounts ? (summary.phase2 / summary.accounts) * 100 : 0}%` }} /></i></div>
            <div><span>Phase 1 <b>{summary.phase1}</b></span><i><em style={{ width: `${summary.accounts ? (summary.phase1 / summary.accounts) * 100 : 0}%` }} /></i></div>
            <div><span>Breached <b>{summary.breached}</b></span><i><em className="red-bar" style={{ width: `${summary.accounts ? (summary.breached / summary.accounts) * 100 : 0}%` }} /></i></div>
          </div>
        </section>
        <section className="panel quick-panel">
          <div className="panel-heading"><div><span className="eyebrow">Quick actions</span><h3>Keep moving</h3></div><Sparkles size={18} className="gold-icon" /></div>
          <button onClick={() => setActive("clients")} data-testid="quick-create-client"><Users size={16} /><span><b>Create client</b><small>Invite a new account holder</small></span><ChevronRight size={15} /></button>
          <button onClick={() => setActive("accounts")} data-testid="quick-update-account"><Gauge size={16} /><span><b>Update balance / P&L</b><small>P&L auto-calculates from balance</small></span><ChevronRight size={15} /></button>
          <button onClick={() => setActive("audit")} data-testid="quick-audit"><ShieldCheck size={16} /><span><b>Review audit trail</b><small>See every change</small></span><ChevronRight size={15} /></button>
        </section>
      </div>
      <section className="panel table-panel">
        <div className="panel-heading"><div><span className="eyebrow">Latest records</span><h3>Recently updated accounts</h3></div><button className="text-button" onClick={() => setActive("accounts")} data-testid="admin-see-accounts">View accounts <ArrowUpRight size={15} /></button></div>
        <DataTable accounts={accounts.slice(0, 5)} />
      </section>
    </>
  );
}

function DataTable({ accounts }) {
  return (
    <div className="data-table">
      <div className="table-head"><span>Account</span><span>Client</span><span>Balance</span><span>P&L</span><span>Status</span></div>
      {accounts.map((a) => (
        <div className="table-row" key={a.id}>
          <div><b>{a.prop_firm}</b><small>{a.account_name}</small></div>
          <span>{a.client_name || "—"}</span>
          <b>{money(a.current_balance)}</b>
          <b className={a.pnl >= 0 ? "positive" : "negative"}>{signedMoney(a.pnl)}</b>
          <StatusBadge>{a.status}</StatusBadge>
        </div>
      ))}
    </div>
  );
}

function AdminClients({ clients, onRefresh }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await request("/clients", { method: "POST", body: JSON.stringify(form) });
      toast.success("Client invitation created");
      setForm({ name: "", email: "", password: "" });
      onRefresh();
    } catch (err) { toast.error(err.message); }
  };
  const removeClient = async (client) => {
    if (!window.confirm(`Delete ${client.name}? This removes the client's login, every account they own, and all related history. This cannot be undone.`)) return;
    try {
      await request(`/clients/${client.id}`, { method: "DELETE" });
      toast.success("Client removed");
      onRefresh();
    } catch (err) { toast.error(err.message); }
  };
  return (
    <>
      <div className="page-intro">
        <div>
          <div className="eyebrow">Administration</div>
          <h1>Client <em>directory.</em></h1>
          <p>Create invitation-only access and keep every relationship organized.</p>
        </div>
      </div>
      <div className="admin-form-layout">
        <section className="panel form-panel">
          <div className="panel-heading"><div><span className="eyebrow">New invitation</span><h3>Create client access</h3></div><Users size={18} className="gold-icon" /></div>
          <form onSubmit={submit} className="stack-form">
            <label>Full name<input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Client name" required /></label>
            <label>Email address<input data-testid="client-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="client@email.com" required /></label>
            <label>Temporary password<input data-testid="client-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" required minLength="8" /></label>
            <button className="gold-button" data-testid="create-client-submit">Create invitation <Plus size={15} /></button>
          </form>
        </section>
        <section className="panel client-list">
          <div className="panel-heading"><div><span className="eyebrow">{clients.length} profiles</span><h3>Active relationships</h3></div><Search size={18} className="gold-icon" /></div>
          {clients.map((client) => (
            <div className="client-row" key={client.id} data-testid={`client-row-${client.id}`}>
              <div className="avatar large">{client.name[0]}</div>
              <div><b>{client.name}</b><small>{client.email}</small></div>
              <StatusBadge>{client.status}</StatusBadge>
              <button className="mini-button danger" onClick={() => removeClient(client)} data-testid={`delete-client-${client.id}`}><Trash2 size={13} /> Delete</button>
            </div>
          ))}
        </section>
      </div>
    </>
  );
}

const CERT_CATEGORIES = [
  { id: "PHASE 1", label: "Phase 1 completion" },
  { id: "PHASE 2", label: "Phase 2 completion" },
  { id: "FUNDED", label: "Funded certificate" },
  { id: "PAYOUT", label: "Payout certificate" },
];

function AccountEditor({ account, onClose, onSaved }) {
  const [form, setForm] = useState({
    prop_firm: account.prop_firm || "",
    account_name: account.account_name || "",
    account_size: account.account_size ?? "",
    starting_balance: account.starting_balance ?? "",
    current_balance: account.current_balance ?? "",
    current_equity: account.current_equity ?? "",
    current_drawdown: account.current_drawdown ?? "",
    remaining_drawdown: account.remaining_drawdown ?? "",
    profit_target: account.profit_target ?? "",
    trading_days: account.trading_days ?? "",
    stage: account.stage || "PHASE 1",
    status: account.status || "ACTIVE",
    notes: account.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [certs, setCerts] = useState([]);
  const [certForm, setCertForm] = useState({ stage: "PHASE 1", title: "Phase 1 completion", description: "", file: null });
  const [uploading, setUploading] = useState(false);

  const loadCerts = useCallback(async () => {
    try {
      const data = await request(`/accounts/${account.id}/achievements`);
      setCerts(data);
    } catch (err) { toast.error(err.message); }
  }, [account.id]);
  useEffect(() => { loadCerts(); }, [loadCerts]);

  const start = Number(form.starting_balance) || 0;
  const current = Number(form.current_balance) || 0;
  const previewPnl = start ? current - start : 0;
  const previewPct = start ? ((current - start) / start) * 100 : 0;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        prop_firm: form.prop_firm,
        account_name: form.account_name,
        account_size: Number(form.account_size) || 0,
        starting_balance: Number(form.starting_balance) || 0,
        current_balance: Number(form.current_balance) || 0,
        current_equity: Number(form.current_equity) || 0,
        current_drawdown: Number(form.current_drawdown) || 0,
        remaining_drawdown: Number(form.remaining_drawdown) || 0,
        profit_target: Number(form.profit_target) || 0,
        trading_days: Number(form.trading_days) || 0,
        stage: form.stage,
        status: form.status,
        notes: form.notes,
      };
      await request(`/accounts/${account.id}`, { method: "PATCH", body: JSON.stringify(payload) });
      toast.success("Account saved — P&L auto-calculated");
      onSaved();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const uploadCert = async (e) => {
    e.preventDefault();
    if (!certForm.file) { toast.error("Choose a certificate file to upload."); return; }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("account_id", account.id);
      data.append("stage", certForm.stage);
      data.append("title", certForm.title || CERT_CATEGORIES.find((c) => c.id === certForm.stage)?.label || "Certificate");
      data.append("description", certForm.description || "");
      data.append("file", certForm.file);
      await request("/achievements", { method: "POST", body: data });
      toast.success("Certificate uploaded");
      setCertForm({ stage: "PHASE 1", title: "Phase 1 completion", description: "", file: null });
      const input = document.getElementById("cert-file-input");
      if (input) input.value = "";
      loadCerts();
      onSaved(false);
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };

  const removeCert = async (cert) => {
    if (!window.confirm(`Delete "${cert.title}"?`)) return;
    try {
      await request(`/achievements/${cert.id}`, { method: "DELETE" });
      toast.success("Certificate removed");
      loadCerts();
      onSaved(false);
    } catch (err) { toast.error(err.message); }
  };

  const viewCert = (cert) => {
    if (!cert.file_id) { toast.info("No file attached to this certificate."); return; }
    window.open(`${API}/files/${cert.file_id}`, "_blank");
  };

  return (
    <div className="modal-backdrop">
      <section className="modal-panel wide">
        <div className="modal-heading">
          <div>
            <span className="eyebrow">{account.client_name || "Managed account"}</span>
            <h3>Edit account & certificates</h3>
          </div>
          <button className="icon-button" onClick={onClose} data-testid="close-account-modal"><X size={17} /></button>
        </div>
        <form onSubmit={save} className="form-grid">
          <label>Prop firm<input data-testid="edit-prop-firm-input" value={form.prop_firm} onChange={(e) => setForm({ ...form, prop_firm: e.target.value })} required minLength="2" /></label>
          <label>Account name<input data-testid="edit-account-name-input" value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} required minLength="2" /></label>
          <label>Account size (USD)<input data-testid="edit-account-size-input" type="number" min="0" value={form.account_size} onChange={(e) => setForm({ ...form, account_size: e.target.value })} /></label>
          <label>Starting balance (USD)<input data-testid="edit-starting-balance-input" type="number" min="0" value={form.starting_balance} onChange={(e) => setForm({ ...form, starting_balance: e.target.value })} /></label>
          <label>Current balance (USD)<input data-testid="edit-balance-input" type="number" min="0" value={form.current_balance} onChange={(e) => setForm({ ...form, current_balance: e.target.value })} /></label>
          <label>Current equity (USD)<input data-testid="edit-equity-input" type="number" min="0" value={form.current_equity} onChange={(e) => setForm({ ...form, current_equity: e.target.value })} /></label>
          <label>Profit target (USD)<input data-testid="edit-target-input" type="number" min="0" value={form.profit_target} onChange={(e) => setForm({ ...form, profit_target: e.target.value })} /></label>
          <label>Current drawdown (USD)<input data-testid="edit-drawdown-input" type="number" min="0" value={form.current_drawdown} onChange={(e) => setForm({ ...form, current_drawdown: e.target.value })} /></label>
          <label>Remaining drawdown (USD)<input data-testid="edit-remaining-drawdown-input" type="number" min="0" value={form.remaining_drawdown} onChange={(e) => setForm({ ...form, remaining_drawdown: e.target.value })} /></label>
          <label>Trading days<input data-testid="edit-trading-days-input" type="number" min="0" value={form.trading_days} onChange={(e) => setForm({ ...form, trading_days: e.target.value })} /></label>
          <label>Stage<select data-testid="edit-stage-select" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}><option>PHASE 1</option><option>PHASE 2</option><option>FUNDED</option></select></label>
          <label>Status<select data-testid="edit-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{["ACTIVE", "PASSED", "FUNDED", "FAILED", "BREACHED", "ARCHIVED"].map((s) => <option key={s}>{s}</option>)}</select></label>
          <div className="form-span pnl-preview" data-testid="pnl-preview">
            <span>Auto-calculated P&L</span>
            <b className={previewPnl >= 0 ? "positive" : "negative"}>{signedMoney(previewPnl)}</b>
            <small className={previewPct >= 0 ? "positive" : "negative"}>{pct(previewPct)} return</small>
          </div>
          <button type="submit" className="gold-button full form-span" data-testid="save-account-update" disabled={saving}>{saving ? "Saving…" : "Save account changes"} <ShieldCheck size={15} /></button>
        </form>

        <div className="cert-section">
          <div className="section-heading">
            <div><span className="eyebrow">Private uploads</span><h3>Certificates</h3></div>
            <span className="result-count">{certs.length} on file</span>
          </div>
          <form onSubmit={uploadCert} className="cert-form" data-testid="cert-upload-form">
            <label>Type
              <select data-testid="cert-stage-select" value={certForm.stage} onChange={(e) => setCertForm({ ...certForm, stage: e.target.value, title: CERT_CATEGORIES.find((c) => c.id === e.target.value)?.label || certForm.title })}>
                {CERT_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </label>
            <label>Title
              <input data-testid="cert-title-input" value={certForm.title} onChange={(e) => setCertForm({ ...certForm, title: e.target.value })} placeholder="Certificate title" required />
            </label>
            <label>Description (optional)
              <input data-testid="cert-description-input" value={certForm.description} onChange={(e) => setCertForm({ ...certForm, description: e.target.value })} placeholder="Note visible to the client" />
            </label>
            <label>File (PDF, PNG, JPG or DOCX up to 10MB)
              <input id="cert-file-input" data-testid="cert-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.docx" onChange={(e) => setCertForm({ ...certForm, file: e.target.files?.[0] || null })} required />
            </label>
            <button className="gold-button" data-testid="cert-upload-submit" disabled={uploading}>{uploading ? "Uploading…" : "Upload certificate"} <Plus size={14} /></button>
          </form>
          <div className="cert-list" data-testid="cert-list">
            {certs.length ? certs.map((cert) => (
              <div className="cert-row" key={cert.id} data-testid={`cert-row-${cert.id}`}>
                <span className={`cert-tag cert-tag-${cert.stage.toLowerCase().replaceAll(" ", "-")}`}>{cert.stage}</span>
                <div className="cert-body">
                  <b>{cert.title}</b>
                  <small>{cert.description || (cert.file_name ? `File: ${cert.file_name}` : "Placeholder — no file attached")}</small>
                </div>
                <small className="cert-date">{new Date(cert.created_at).toLocaleDateString()}</small>
                <div className="row-actions">
                  {cert.file_id && <button type="button" className="mini-button gold" onClick={() => viewCert(cert)} data-testid={`cert-view-${cert.id}`}>View</button>}
                  <button type="button" className="mini-button danger" onClick={() => removeCert(cert)} data-testid={`cert-delete-${cert.id}`}><Trash2 size={12} /></button>
                </div>
              </div>
            )) : <Empty text="No certificates uploaded yet. Add Phase 1, Phase 2, Funded or Payout certificates above." />}
          </div>
        </div>
      </section>
    </div>
  );
}

function AdminAccounts({ accounts, clients, onRefresh }) {
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    client_id: "",
    prop_firm: "",
    account_name: "",
    reference: "",
    account_size: "",
    starting_balance: "",
    current_balance: "",
    stage: "PHASE 1",
    status: "ACTIVE",
  });
  const filtered = accounts.filter((a) => `${a.prop_firm} ${a.account_name} ${a.client_name || ""}`.toLowerCase().includes(search.toLowerCase()));

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      const size = Number(createForm.account_size) || 0;
      const start = Number(createForm.starting_balance) || size;
      const current = Number(createForm.current_balance) || start;
      const pnl = current - start;
      const pnl_pct = start ? (pnl / start) * 100 : 0;
      await request("/accounts", {
        method: "POST",
        body: JSON.stringify({
          client_id: createForm.client_id,
          prop_firm: createForm.prop_firm,
          account_name: createForm.account_name,
          reference: createForm.reference,
          account_size: size,
          starting_balance: start,
          current_balance: current,
          current_equity: current,
          pnl,
          pnl_pct: Math.max(-100, Math.min(1000, pnl_pct)),
          profit_target: 0,
          current_drawdown: 0,
          maximum_drawdown: 0,
          daily_drawdown: 0,
          remaining_drawdown: 0,
          trading_days: 0,
          stage: createForm.stage,
          status: createForm.status,
          payout_cycle: "N/A",
          notes: "",
        }),
      });
      toast.success("Account created");
      setCreating(false);
      setCreateForm({ client_id: "", prop_firm: "", account_name: "", reference: "", account_size: "", starting_balance: "", current_balance: "", stage: "PHASE 1", status: "ACTIVE" });
      onRefresh();
    } catch (err) { toast.error(err.message); }
  };

  const removeAccount = async (a) => {
    if (!window.confirm(`Delete ${a.prop_firm} · ${a.account_name}? All snapshots and history for this account will be removed.`)) return;
    try {
      await request(`/accounts/${a.id}`, { method: "DELETE" });
      toast.success("Account removed");
      onRefresh();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <>
      <div className="page-intro">
        <div>
          <div className="eyebrow">Administration</div>
          <h1>Account <em>updates.</em></h1>
          <p>Rename the prop firm, edit balances, add dated snapshots — the client sees it all automatically.</p>
        </div>
        <div className="intro-actions">
          <button className="outline-button" onClick={() => window.open(`${API}/exports/accounts`, "_blank")} data-testid="export-accounts-button"><Download size={15} /> Export CSV</button>
          <button className="gold-button" onClick={() => setCreating(true)} data-testid="create-account-button"><Plus size={15} /> New account</button>
        </div>
      </div>
      <div className="filter-bar">
        <div className="search-box"><Search size={16} /><input data-testid="admin-account-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client, firm or account" /></div>
        <span className="result-count">{filtered.length} of {accounts.length}</span>
      </div>
      <section className="panel table-panel">
        <DataTable accounts={filtered} />
        <div className="admin-table-actions">
          {filtered.map((a) => (
            <div className="admin-table-action-row" key={a.id}>
              <button className="text-button" onClick={() => setEditing(a)} data-testid={`edit-account-${a.id}`}>Edit {a.prop_firm} · {a.account_name} <ArrowUpRight size={14} /></button>
              <button className="mini-button danger" onClick={() => removeAccount(a)} data-testid={`delete-account-${a.id}`}><Trash2 size={12} /> Delete</button>
            </div>
          ))}
        </div>
      </section>
      {editing && <AccountEditor account={editing} onClose={() => setEditing(null)} onSaved={(close = true) => { onRefresh(); if (close) setEditing(null); }} />}
      {creating && (
        <div className="modal-backdrop">
          <section className="modal-panel">
            <div className="modal-heading"><div><span className="eyebrow">New account</span><h3>Add account</h3></div><button className="icon-button" onClick={() => setCreating(false)} data-testid="close-create-modal"><X size={17} /></button></div>
            <form onSubmit={submitCreate} className="form-grid">
              <label className="form-span">Client<select data-testid="create-client-select" value={createForm.client_id} onChange={(e) => setCreateForm({ ...createForm, client_id: e.target.value })} required><option value="">Select client…</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
              <label>Prop firm<input data-testid="create-prop-firm" value={createForm.prop_firm} onChange={(e) => setCreateForm({ ...createForm, prop_firm: e.target.value })} required minLength="2" /></label>
              <label>Account name<input data-testid="create-account-name" value={createForm.account_name} onChange={(e) => setCreateForm({ ...createForm, account_name: e.target.value })} required minLength="2" /></label>
              <label>Reference<input data-testid="create-reference" value={createForm.reference} onChange={(e) => setCreateForm({ ...createForm, reference: e.target.value })} /></label>
              <label>Account size (USD)<input data-testid="create-account-size" type="number" min="1" value={createForm.account_size} onChange={(e) => setCreateForm({ ...createForm, account_size: e.target.value })} required /></label>
              <label>Starting balance (USD)<input data-testid="create-starting-balance" type="number" min="0" value={createForm.starting_balance} onChange={(e) => setCreateForm({ ...createForm, starting_balance: e.target.value })} /></label>
              <label>Current balance (USD)<input data-testid="create-current-balance" type="number" min="0" value={createForm.current_balance} onChange={(e) => setCreateForm({ ...createForm, current_balance: e.target.value })} /></label>
              <label>Stage<select data-testid="create-stage" value={createForm.stage} onChange={(e) => setCreateForm({ ...createForm, stage: e.target.value })}><option>PHASE 1</option><option>PHASE 2</option><option>FUNDED</option></select></label>
              <label>Status<select data-testid="create-status" value={createForm.status} onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}>{["ACTIVE", "PASSED", "FUNDED", "FAILED", "BREACHED", "ARCHIVED"].map((s) => <option key={s}>{s}</option>)}</select></label>
              <button className="gold-button full form-span" data-testid="create-account-submit">Create account <Plus size={15} /></button>
            </form>
          </section>
        </div>
      )}
    </>
  );
}

function AdminAudit({ audit }) {
  return (
    <>
      <div className="page-intro">
        <div>
          <div className="eyebrow">Governance</div>
          <h1>Audit <em>trail.</em></h1>
          <p>A transparent record of every material portfolio action.</p>
        </div>
      </div>
      <section className="panel audit-panel">
        {audit.length ? audit.map((item) => (
          <div className="audit-row" key={item.id}>
            <div className="audit-dot" />
            <div><b>{item.action.replaceAll("_", " ")}</b><small>{item.actor} · {new Date(item.created_at).toLocaleString()}</small></div>
            <span>{item.account_id ? "Account record" : item.client_id ? "Client record" : "Portal"}</span>
          </div>
        )) : <Empty text="Audit events will appear here." />}
      </section>
    </>
  );
}

function AppInner() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [active, setActive] = useState("overview");
  const [accounts, setAccounts] = useState([]);
  const [clients, setClients] = useState([]);
  const [notes, setNotes] = useState([]);
  const [audit, setAudit] = useState([]);
  const [summary, setSummary] = useState({ clients: 0, accounts: 0, account_size: 0, balance: 0, pnl: 0, funded: 0, phase1: 0, phase2: 0, breached: 0 });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const selectedRef = useRef(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  useEffect(() => {
    document.title = "VENTREX — Private Client Portal";
    request("/auth/me").then(setUser).catch(() => setUser(false)).finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    if (user.role === "admin" && !["admin", "clients", "accounts", "audit"].includes(active)) setActive("admin");
    if (user.role === "client" && !["overview", "accounts", "performance", "achievements", "detail"].includes(active)) setActive("overview");
  }, [user, active]);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const [allAccounts, allNotes] = await Promise.all([request("/accounts"), request("/notes")]);
      setAccounts(allAccounts);
      setNotes(allNotes);
      if (user.role === "admin") {
        const [stats, allClients, logs] = await Promise.all([request("/admin/summary"), request("/clients"), request("/audit")]);
        setSummary(stats);
        setClients(allClients);
        setAudit(logs);
      }
      const current = selectedRef.current;
      if (current) {
        try {
          const d = await request(`/accounts/${current.id}/detail`);
          setDetail(d);
          setSelected(d.account);
        } catch (err) {
          console.warn("Unable to refresh selected account detail", err);
        }
      }
    } catch (err) { toast.error(err.message); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const openAccount = async (account) => {
    try {
      setSelected(account);
      const d = await request(`/accounts/${account.id}/detail`);
      setDetail(d);
      setActive("detail");
    } catch (err) { toast.error(err.message); }
  };

  const logout = async () => {
    await request("/auth/logout", { method: "POST" });
    setUser(false);
    setActive("overview");
  };

  if (checking) {
    return (
      <div className="loading-screen">
        <Candles />
        <div className="brand-mark">V</div>
        <b>VENTREX</b>
        <span>Preparing your private workspace…</span>
      </div>
    );
  }

  if (!user) return <Login onLogin={(loggedIn) => setUser(loggedIn)} />;

  let content;
  if (user.role === "admin") {
    if (active === "admin") content = <AdminDashboard summary={summary} accounts={accounts} setActive={setActive} />;
    else if (active === "clients") content = <AdminClients clients={clients} onRefresh={refresh} />;
    else if (active === "accounts") content = <AdminAccounts accounts={accounts} clients={clients} onRefresh={refresh} />;
    else content = <AdminAudit audit={audit} />;
  } else {
    if (active === "overview") content = <ClientOverview user={user} accounts={accounts} notes={notes} setActive={setActive} onOpen={openAccount} />;
    else if (active === "accounts") content = <ClientAccounts accounts={accounts} onOpen={openAccount} />;
    else if (active === "performance") content = <ClientPerformance accounts={accounts} onOpen={openAccount} />;
    else if (active === "achievements") content = <ClientAchievements accounts={accounts} onOpen={openAccount} />;
    else if (active === "detail" && selected) content = <AccountDetail account={selected} detail={detail} onBack={() => setActive("accounts")} />;
    else content = <ClientOverview user={user} accounts={accounts} notes={notes} setActive={setActive} onOpen={openAccount} />;
  }

  return (
    <>
      <Shell user={user} active={active} setActive={setActive} onLogout={logout}>{content}</Shell>
      <Toaster theme="dark" position="bottom-right" />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}
